/*
  OSMODI floating capsule header, draft theme 190583472421.
  Revised 2026-08-12 against the owner's reference store.

  In the reference every header control opens a panel inside the same capsule
  surface, and that surface animates its height from the bar to the panel while
  the whole page behind is blurred. This file drives that: it owns the open
  panel, the surface height, the drill down levels and the blurred backdrop.

  Cart is deliberately not handled here. It keeps the existing OSMODI cart
  drawer through data-v4-cart-toggle, which assets/osmodi-v4.js already drives.

  No price, policy, shipping term, return term, identity value, currency value,
  variant association or structured-data value is changed here. The country
  buttons submit Shopify's own localization form and this file never rewrites a
  currency.

  Contains no Unicode em dash characters.
*/

(() => {
  'use strict';

  const DISMISS_KEY = 'osmodi:capsule-announcement-dismissed';
  const MORPH_MS = 350;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function initHeader(header) {
    if (!header || header.dataset.chReady === 'true') return;
    header.dataset.chReady = 'true';

    const surface = header.querySelector('[data-ch-surface]');
    const capsule = header.querySelector('.ch__capsule');
    const backdrop = header.querySelector('[data-ch-backdrop]');
    const panels = Array.from(header.querySelectorAll('[data-ch-panel]'));
    const triggers = Array.from(header.querySelectorAll('[data-ch-open]'));
    if (!surface || !capsule) return;

    let openName = null;
    let returnFocus = null;
    let morphTimer = 0;

    const panelFor = (name) => panels.find((p) => p.dataset.chPanel === name) || null;

    // The surface is animated from its current pixel height to the height the
    // requested content needs. A plain auto height cannot be transitioned, so
    // the target is measured while the panel is laid out but before the
    // animation starts.
    const morphTo = (targetHeight) => {
      window.clearTimeout(morphTimer);
      const current = surface.getBoundingClientRect().height;
      surface.style.height = current + 'px';
      // Force a style flush so the browser treats the next value as a change.
      void surface.offsetHeight;
      surface.style.height = targetHeight + 'px';
      morphTimer = window.setTimeout(() => {
        // Release the fixed height once settled, so later content changes and
        // viewport resizes do not fight a stale pixel value.
        if (openName) surface.style.height = 'auto';
        else surface.style.height = '';
        publishOffset();
      }, MORPH_MS + 30);
    };

    const showLevel = (panel, levelName) => {
      const levels = Array.from(panel.querySelectorAll('[data-ch-level]'));
      levels.forEach((level) => {
        level.hidden = level.dataset.chLevel !== levelName;
      });
      panel.dataset.chCurrentLevel = levelName;
    };

    const closePanel = (restoreFocus = true) => {
      if (!openName) return;
      const panel = panelFor(openName);
      openName = null;
      delete header.dataset.chActive;
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('ch-locked');

      const capsuleHeight = capsule.getBoundingClientRect().height;
      morphTo(capsuleHeight);

      window.setTimeout(() => {
        if (panel) panel.hidden = true;
        if (backdrop) backdrop.hidden = true;
        if (!openName) surface.style.height = '';
        publishOffset();
      }, MORPH_MS);

      if (restoreFocus && returnFocus && returnFocus.isConnected) {
        returnFocus.focus({ preventScroll: true });
      }
      returnFocus = null;
    };

    const openPanel = (name, trigger) => {
      const panel = panelFor(name);
      if (!panel) return;
      if (openName === name) {
        closePanel();
        return;
      }

      const previous = openName ? panelFor(openName) : null;
      if (previous && previous !== panel) previous.hidden = true;

      returnFocus = trigger || document.activeElement;
      openName = name;
      // Deliberately not data-ch-open. The triggers carry that attribute, and a
      // click inside an open panel would otherwise match the header itself
      // through closest() and toggle the panel shut instead of following the
      // link the shopper actually clicked.
      header.dataset.chActive = name;

      if (backdrop) backdrop.hidden = false;
      panel.hidden = false;

      if (panel.querySelector('[data-ch-level]')) showLevel(panel, 'root');

      triggers.forEach((t) => t.setAttribute('aria-expanded', String(t.dataset.chOpen === name)));
      document.body.classList.add('ch-locked');

      // Measure the natural height with the panel laid out, then animate to it.
      surface.style.height = 'auto';
      const target = surface.getBoundingClientRect().height;
      morphTo(target);

      window.requestAnimationFrame(() => {
        const first = panel.querySelector('[data-ch-search-input]') || panel.querySelector(focusableSelector);
        if (first) first.focus({ preventScroll: true });
      });
    };

    header.addEventListener('click', (event) => {
      const trigger = event.target.closest('button[data-ch-open]');
      if (trigger) {
        event.preventDefault();
        openPanel(trigger.dataset.chOpen, trigger);
        return;
      }

      if (event.target.closest('[data-ch-close]')) {
        event.preventDefault();
        closePanel();
        return;
      }

      const drill = event.target.closest('[data-ch-drill]');
      if (drill) {
        event.preventDefault();
        const panel = drill.closest('[data-ch-panel]');
        if (panel) {
          showLevel(panel, drill.dataset.chDrill);
          surface.style.height = 'auto';
          morphTo(surface.getBoundingClientRect().height);
          const back = panel.querySelector('[data-ch-level]:not([hidden]) [data-ch-back]');
          if (back) back.focus({ preventScroll: true });
        }
        return;
      }

      if (event.target.closest('[data-ch-back]')) {
        event.preventDefault();
        const panel = event.target.closest('[data-ch-panel]');
        if (panel) {
          showLevel(panel, 'root');
          surface.style.height = 'auto';
          morphTo(surface.getBoundingClientRect().height);
        }
      }
    });

    if (backdrop) {
      backdrop.addEventListener('click', () => closePanel());
    }

    document.addEventListener('keydown', (event) => {
      if (!openName) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
        return;
      }
      if (event.key !== 'Tab') return;

      // Keep focus inside the open panel and its capsule row, since the panel
      // is a modal surface over a blurred page.
      const scope = surface;
      const items = Array.from(scope.querySelectorAll(focusableSelector)).filter((el) => {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });

    // Opening the bag drawer must not leave a header panel open behind it.
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-v4-cart-toggle]')) closePanel(false);
    });

    /* ---------------- Content offset ---------------- */

    // The capsule is fixed, so page content needs a matching offset. The
    // announcement is a theme setting of any length and wraps on narrow phones,
    // so a hardcoded offset is wrong as soon as the copy changes. This publishes
    // the measured height as --ch-offset and the stylesheet consumes it, falling
    // back to its own value if this never runs.
    const announcement = header.querySelector('[data-ch-announcement]');

    function publishOffset() {
      const CLEARANCE = 8;
      // Built from the capsule's bottom plus the announcement's own height,
      // never from the announcement's position. When a panel opens, the surface
      // grows and pushes the announcement down the viewport, so measuring where
      // the announcement sits would inflate the offset and shove every page
      // below it down by the height of the open panel.
      let height = capsule.getBoundingClientRect().bottom;

      if (announcement && !announcement.hidden) {
        const gap = parseFloat(window.getComputedStyle(header).rowGap) || 0;
        height += gap + announcement.getBoundingClientRect().height;
      }

      document.documentElement.style.setProperty('--ch-offset', Math.ceil(height) + CLEARANCE + 'px');
    }

    publishOffset();

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(publishOffset);
      observer.observe(capsule);
      if (announcement) observer.observe(announcement);
    }

    window.addEventListener('resize', () => {
      if (openName) surface.style.height = 'auto';
      publishOffset();
    }, { passive: true });
    window.addEventListener('orientationchange', publishOffset, { passive: true });

    /* ---------------- Announcement ---------------- */

    // Session scoped on purpose. The island carries a shipping term, so a
    // shopper who dismisses it should see it again on their next visit rather
    // than having it hidden permanently.
    if (announcement) {
      let dismissed = false;
      try {
        dismissed = window.sessionStorage.getItem(DISMISS_KEY) === 'true';
      } catch (error) {
        dismissed = false;
      }

      if (dismissed) {
        announcement.hidden = true;
        publishOffset();
      }

      const closeButton = announcement.querySelector('[data-ch-announcement-close]');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          announcement.hidden = true;
          publishOffset();
          try {
            window.sessionStorage.setItem(DISMISS_KEY, 'true');
          } catch (error) {
            // A blocked storage write only means the notice returns on the next
            // page view, which is the safe direction for a shipping term.
          }
        });
      }
    }

    /* ---------------- Search field ---------------- */

    // The clear button only exists once there is something to clear, so the
    // field does not carry a dead control. Clearing returns focus to the input
    // rather than dropping it, which is what a shopper expects mid query.
    const searchInput = header.querySelector('[data-ch-search-input]');
    const searchClear = header.querySelector('[data-ch-search-clear]');
    if (searchInput && searchClear) {
      const syncClear = () => {
        searchClear.hidden = searchInput.value.trim().length === 0;
      };
      searchInput.addEventListener('input', syncClear);
      searchClear.addEventListener('click', () => {
        searchInput.value = '';
        syncClear();
        searchInput.focus({ preventScroll: true });
      });
      syncClear();
    }

    /* ---------------- Scroll behaviour ---------------- */

    // Only runs when the setting asks for it. The header is never hidden while a
    // panel is open, because the panel is a modal surface and pulling it off
    // screen would strand the shopper. The threshold keeps small scrolls and
    // rubber banding from flickering the header.
    if (header.dataset.chScroll === 'hide') {
      const THRESHOLD = 8;
      const REVEAL_ABOVE = 90;
      let lastY = window.scrollY;
      let ticking = false;

      const applyScroll = () => {
        const y = window.scrollY;
        const delta = y - lastY;

        if (openName || y < REVEAL_ABOVE) {
          header.dataset.chHidden = 'false';
          lastY = y;
          return;
        }

        if (Math.abs(delta) < THRESHOLD) return;
        header.dataset.chHidden = delta > 0 ? 'true' : 'false';
        lastY = y;
      };

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          applyScroll();
        });
      }, { passive: true });

      applyScroll();
    }
  }

  function init() {
    document.querySelectorAll('[data-osmodi-capsule-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
